import React from 'react';
import { motion } from 'framer-motion';
import { SquaresFour, List } from '@phosphor-icons/react';
import { gameSystems, getSymbol } from '../data/gameSystems';
import { getCoinIconUrl } from '../data/coinIcons';
import { useTranslation } from '../hooks/useTranslation';

const coinColors = {
  copper: { bg: 'bg-amber-700/10', text: 'text-amber-700', border: 'border-amber-700/20' },
  silver: { bg: 'bg-slate-400/10', text: 'text-slate-400', border: 'border-slate-400/20' },
  electrum: { bg: 'bg-cyan-400/10', text: 'text-cyan-400', border: 'border-cyan-400/20' },
  gold: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/20' },
  platinum: { bg: 'bg-indigo-400/10', text: 'text-indigo-400', border: 'border-indigo-400/20' },
  brass: { bg: 'bg-orange-700/10', text: 'text-orange-700', border: 'border-orange-700/20' },
};

export const CoinDisplay = ({ wallet, systemId, viewMode, onViewModeChange }) => {
  const { lang } = useTranslation();
  const system = gameSystems[systemId];
  const sortedCurrencies = [...system.currencies].sort((a, b) => b.valueInBase - a.valueInBase);
  
  return (
    <div>
      {/* View Toggle */}
      <div className="flex justify-end mb-3">
        <div className="flex bg-[var(--background)] rounded-lg p-1">
          <button
            onClick={() => onViewModeChange('tiles')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'tiles' 
                ? 'bg-[var(--surface)] text-[var(--text)]' 
                : 'text-[var(--text-muted)]'
            }`}
            data-testid="view-tiles"
          >
            <SquaresFour size={18} weight={viewMode === 'tiles' ? 'fill' : 'regular'} />
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'list' 
                ? 'bg-[var(--surface)] text-[var(--text)]' 
                : 'text-[var(--text-muted)]'
            }`}
            data-testid="view-list"
          >
            <List size={18} weight={viewMode === 'list' ? 'fill' : 'regular'} />
          </button>
        </div>
      </div>
      
      {/* Tiles View */}
      {viewMode === 'tiles' && (
        <div className="grid grid-cols-2 gap-3">
          {sortedCurrencies.map((currency, index) => {
            const colors = coinColors[currency.colorKey] || coinColors.gold;
            const amount = wallet[currency.id] || 0;
            
            const iconUrl = getCoinIconUrl(currency.id);
            return (
              <motion.div
                key={currency.id}
                data-testid={`coin-display-${currency.id}`}
                className={`relative p-3 sm:p-4 rounded-2xl ${colors.bg} border ${colors.border} overflow-hidden`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="relative z-10 flex items-center gap-3">
                  {iconUrl ? (
                    <img
                      src={iconUrl}
                      alt=""
                      className="h-11 w-11 sm:h-12 sm:w-12 shrink-0 object-contain pointer-events-none select-none"
                      aria-hidden
                    />
                  ) : (
                    <span
                      className={`h-11 w-11 sm:h-12 sm:w-12 shrink-0 flex items-center justify-center text-lg font-bold ${colors.text} opacity-40`}
                      aria-hidden
                    >
                      {getSymbol(currency, lang)}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className={`text-2xl sm:text-3xl font-bold leading-tight ${colors.text}`}>
                      {amount.toLocaleString()}
                    </p>
                    <p className={`text-xs sm:text-sm font-medium ${colors.text} opacity-80 truncate`}>
                      {lang === 'it' ? currency.nameIt : currency.name}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      
      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-1.5">
          {sortedCurrencies.map((currency, index) => {
            const colors = coinColors[currency.colorKey] || coinColors.gold;
            const amount = wallet[currency.id] || 0;
            
            return (
              <motion.div
                key={currency.id}
                data-testid={`coin-display-${currency.id}`}
                className={`flex items-center justify-between px-3 py-2.5 rounded-xl ${colors.bg} border ${colors.border}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold ${colors.text} w-7`}>
                    {getSymbol(currency, lang)}
                  </span>
                  <span className={`text-sm font-medium ${colors.text}`}>
                    {lang === 'it' ? currency.nameIt : currency.name}
                  </span>
                </div>
                
                <span className={`text-lg font-bold ${colors.text}`}>
                  {amount.toLocaleString()}
                </span>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};
